/**
 * Piloter › Vue d'ensemble (/piloter) — indicateurs de rentabilité, meilleures ventes,
 * répartition du stock, sorties par mode, coups de cœur et bouteilles dormantes.
 * Accès gérant/sommelier. Toutes les valeurs monétaires respectent le mode écran public.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Wine, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { piloterApi, caveApi } from '../../services/api';
import { usePublicMode } from '../../context/PublicModeContext';
import { formatEuro, formatInt, formatDate } from '../../utils/format';
import { Card, Spinner, Button } from '../../components/ui/kit';
import { WineColorDot, MaskedValue, Hearts } from '../../components/wine/wine';
import { WINE_COLOR_DOTS } from '../../constants';

const PERIODS = [
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois en cours' },
  { value: 'saison', label: 'Saison' },
  { value: 'perso', label: 'Personnalisée' },
];

/** Sélecteur de période segmenté + plage personnalisée. */
function PeriodSelector({ period, setPeriod, start, end, setStart, setEnd }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="piloter-period">
      <div className="inline-flex rounded-md border border-hair overflow-hidden">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            data-testid={`period-${p.value}`}
            onClick={() => setPeriod(p.value)}
            className={`px-3 h-9 text-[13px] transition-colors border-r border-hair last:border-r-0 ${
              period === p.value ? 'bg-gold100 text-gold800' : 'text-n600 hover:bg-surface'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {period === 'perso' && (
        <div className="flex items-center gap-2">
          <input type="date" className="vd-input h-9 w-[150px]" value={start} onChange={(e) => setStart(e.target.value)} data-testid="period-start" />
          <span className="text-n500">→</span>
          <input type="date" className="vd-input h-9 w-[150px]" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="period-end" />
        </div>
      )}
    </div>
  );
}

/** Un indicateur du bandeau (séparé par des filets). */
function Kpi({ label, children, sub, testId }) {
  return (
    <div className="flex-1 min-w-[160px] px-5 py-4" data-testid={testId}>
      <p className="kicker mb-2">{label}</p>
      <p className="text-4xl leading-none nums">{children}</p>
      {sub && <p className="text-[13px] text-n600 mt-2">{sub}</p>}
    </div>
  );
}

/** Barre horizontale proportionnelle (accent doré). */
function Bar({ ratio, color }) {
  return (
    <span className="block h-2 rounded-full bg-surface overflow-hidden">
      <span className="block h-full rounded-full" style={{ width: `${Math.max(ratio * 100, 2)}%`, background: color || '#facb8d' }} />
    </span>
  );
}

export default function Piloter() {
  const navigate = useNavigate();
  const { isPublic } = usePublicMode();
  const [period, setPeriod] = useState('mois');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const params = { period, public: isPublic };
  if (period === 'perso' && start && end) { params.start = start; params.end = end; }

  const { data, isLoading } = useQuery({
    queryKey: ['piloter-overview', period, start, end, isPublic],
    queryFn: () => piloterApi.overview(params),
    enabled: period !== 'perso' || Boolean(start && end),
  });

  const glassMutation = useMutation({
    mutationFn: ({ referenceId, byGlass }) => caveApi.setFlags(null, { reference_id: referenceId, by_glass: byGlass }),
    onSuccess: () => toast.success('Proposée au verre'),
  });

  if (isLoading || !data) return <div className="p-6"><Spinner /></div>;
  const k = data.kpis;
  const evo = k.ca_evolution;

  return (
    <div className="p-4 md:p-8 max-w-[1200px] space-y-6">
      <PeriodSelector period={period} setPeriod={setPeriod} start={start} end={end} setStart={setStart} setEnd={setEnd} />

      {/* Bandeau des 5 indicateurs */}
      <Card className="flex flex-col sm:flex-row flex-wrap divide-y sm:divide-y-0 sm:divide-x divide-hair" data-testid="piloter-kpis">
        <Kpi testId="kpi-stock-value" label="Valeur d'achat du stock"
          sub={<><span>{formatInt(k.stock_bottles)} btl · PMP </span><MaskedValue masked={isPublic}>{formatEuro(k.pmp_avg)}</MaskedValue></>}>
          <MaskedValue masked={isPublic}>{formatEuro(k.stock_purchase_value, { decimals: 0 })}</MaskedValue>
        </Kpi>
        <Kpi testId="kpi-sale-value" label="Valeur de vente estimée" sub="prix caviste actuels">
          <MaskedValue masked={isPublic}>{formatEuro(k.stock_sale_value, { decimals: 0 })}</MaskedValue>
        </Kpi>
        <Kpi testId="kpi-coefficient" label="Coefficient moyen" sub="prix table ÷ prix d'achat">
          {k.coefficient ? `×${k.coefficient.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` : '—'}
        </Kpi>
        <Kpi testId="kpi-ca" label="CA vins · période"
          sub={evo === null || evo === undefined ? 'pas de période précédente' : (
            <span className={`inline-flex items-center gap-1 ${evo >= 0 ? 'text-gold700' : 'text-n700'}`}>
              {evo >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {evo >= 0 ? '+' : ''}{evo.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} % vs préc.
            </span>
          )}>
          <MaskedValue masked={isPublic}>{formatEuro(k.ca, { decimals: 0 })}</MaskedValue>
        </Kpi>
        <Kpi testId="kpi-margin" label="Marge brute · période" sub={`${k.margin_pct.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} % du CA`}>
          <MaskedValue masked={isPublic}>{formatEuro(k.margin, { decimals: 0 })}</MaskedValue>
        </Kpi>
      </Card>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Meilleures ventes */}
        <section data-testid="best-sellers">
          <h3 className="font-title text-2xl hair-b pb-2 mb-3">Meilleures ventes</h3>
          {data.best_sellers.length === 0 ? <p className="text-sm text-n600 py-4">Aucune sortie sur la période.</p> : (
            <div className="space-y-3">
              {data.best_sellers.map((b, i) => (
                <button key={b.cuvee_id} data-testid={`best-seller-${i}`} onClick={() => navigate(`/cave/cuvee/${b.cuvee_id}`)}
                  className="w-full text-left group">
                  <div className="flex items-center gap-2 mb-1">
                    <WineColorDot color={b.color} size={9} />
                    <span className="font-title text-lg leading-none group-hover:text-gold800 transition-colors">{b.name}</span>
                    {b.hearts > 0 && <Hearts count={b.hearts} size={11} />}
                    <span className="ml-auto nums text-[14px] text-n700">{formatInt(b.bottles)} btl</span>
                  </div>
                  <Bar ratio={b.ratio} color="#c28d41" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Répartition du stock par couleur */}
        <section data-testid="stock-by-color">
          <h3 className="font-title text-2xl hair-b pb-2 mb-3">Répartition du stock</h3>
          <button onClick={() => navigate('/cave/stock')} className="block w-full h-3 rounded-full overflow-hidden flex mb-4" data-testid="stock-color-bar">
            {data.stock_by_color.map((c) => (
              <span key={c.color} style={{ width: `${c.pct}%`, background: WINE_COLOR_DOTS[c.color]?.color || '#999' }} title={`${c.label} ${c.pct}%`} />
            ))}
          </button>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {data.stock_by_color.map((c) => (
              <div key={c.color} className="flex items-center gap-2 text-[13.5px]" data-testid={`color-legend-${c.color}`}>
                <WineColorDot color={c.color} size={9} />
                <span>{c.label}</span>
                <span className="ml-auto nums text-n600">{formatInt(c.bottles)} btl · {c.pct.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</span>
              </div>
            ))}
          </div>
        </section>

        {/* Sorties par mode de vente */}
        <section data-testid="exits-by-mode">
          <h3 className="font-title text-2xl hair-b pb-2 mb-3">Sorties par mode de vente</h3>
          <div className="space-y-2.5">
            {data.exits_by_mode.map((m) => (
              <div key={m.motif} className="flex items-center gap-3 text-[13.5px]" data-testid={`mode-${m.motif}`}>
                <span className="w-28 shrink-0">{m.label}</span>
                <span className="flex-1"><Bar ratio={m.pct / 100} color="#facb8d" /></span>
                <span className="w-24 text-right nums text-n600">{formatInt(m.bottles)} btl · {m.pct.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</span>
              </div>
            ))}
          </div>
        </section>

        {/* Coups de cœur */}
        <section data-testid="hearts-block">
          <h3 className="font-title text-2xl hair-b pb-2 mb-3">Coups de cœur</h3>
          <div className="flex gap-6 mb-3">
            <div><p className="text-3xl nums leading-none">{formatInt(data.hearts.cuvees_count)}</p><p className="text-[12px] text-n600 mt-1">cuvées</p></div>
            <div><p className="text-3xl nums leading-none">{formatInt(data.hearts.stock_bottles)}</p><p className="text-[12px] text-n600 mt-1">bouteilles</p></div>
            <div><p className="text-3xl nums leading-none">{data.hearts.sales_share.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} %</p><p className="text-[12px] text-n600 mt-1">des ventes</p></div>
          </div>
          <div className="space-y-1.5">
            {data.hearts.top.map((t) => (
              <button key={t.cuvee_id} onClick={() => navigate(`/cave/cuvee/${t.cuvee_id}`)} data-testid={`hearts-top-${t.cuvee_id}`}
                className="w-full flex items-center gap-2 text-[13.5px] hover:text-gold800 transition-colors">
                <WineColorDot color={t.color} size={9} />
                <span>{t.name}</span>
                <span className="ml-auto text-n600">{t.per_month.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} sorties / mois</span>
              </button>
            ))}
            {data.hearts.top.length === 0 && <p className="text-sm text-n600">Aucune vente de coup de cœur sur la période.</p>}
          </div>
        </section>
      </div>

      {/* Bouteilles dormantes */}
      <section data-testid="dormant-block">
        <div className="flex items-baseline justify-between hair-b pb-2 mb-3">
          <h3 className="font-title text-2xl">Bouteilles dormantes</h3>
          <span className="text-[13px] text-n500">sans sortie depuis {data.dormant_months} mois</span>
        </div>
        {data.dormant.length === 0 ? <p className="text-sm text-n600 py-4">Aucune référence dormante.</p> : (
          <div className="vd-card overflow-hidden">
            <table className="w-full text-[13.5px]" data-testid="dormant-table">
              <thead>
                <tr className="text-left kicker">
                  <th className="py-2.5 px-3">Cuvée</th><th className="py-2.5 px-3">Dernière sortie</th>
                  <th className="py-2.5 px-3 text-right">Qté</th><th className="py-2.5 px-3 text-right">Valeur immobilisée</th>
                  <th className="py-2.5 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.dormant.map((d) => (
                  <tr key={d.reference_id} className="hair-t" data-testid={`dormant-${d.reference_id}`}>
                    <td className="py-2.5 px-3">
                      <button onClick={() => navigate(`/cave/cuvee/${d.cuvee_id}`)} className="flex items-center gap-2 hover:text-gold800 transition-colors">
                        <WineColorDot color={d.color} size={9} />{d.name} <span className="text-n500">{d.year}</span>
                      </button>
                    </td>
                    <td className="py-2.5 px-3 text-n600 nums">{d.last_exit ? formatDate(d.last_exit) : 'jamais'}</td>
                    <td className="py-2.5 px-3 text-right nums">{formatInt(d.quantity)}</td>
                    <td className="py-2.5 px-3 text-right nums text-n700"><MaskedValue masked={isPublic}>{formatEuro(d.purchase_value)}</MaskedValue></td>
                    <td className="py-2.5 px-3 text-right">
                      {d.by_glass ? (
                        <Button variant="ghost" className="h-7 text-[12px]" onClick={() => navigate(`/cave/cuvee/${d.cuvee_id}`)} data-testid={`dormant-feature-${d.reference_id}`}>
                          <Sparkles className="w-3.5 h-3.5" /> Mettre en avant
                        </Button>
                      ) : (
                        <Button variant="ghost" className="h-7 text-[12px]" onClick={() => glassMutation.mutate({ referenceId: d.reference_id, byGlass: true })} data-testid={`dormant-glass-${d.reference_id}`}>
                          <Wine className="w-3.5 h-3.5" /> Au verre
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
